import { Dialog } from '../controls/dialog.js';
import { AsapPromise } from '../util/asap_promise.js';
import { window } from '../window.js';

/**
 * Change profile avatar dialog.
 */
class ChangeAvatarDialog extends Dialog {
  initialize() {
    super.initialize();

    this.picFrameContainerElement.addEventListener('mousedown', ev => {
      this.#moving = true;
      this.#movingX = ev.clientX;
      this.#movingY = ev.clientY;
    });

    document.addEventListener('mouseup', () => {
      this.#moving = false;
    });

    this.containerElement.addEventListener('mousemove', ev => {
      if (this.#moving) {
        let deltaX = ev.clientX - this.#movingX;
        let deltaY = ev.clientY - this.#movingY;

        let picStyle = window.getComputedStyle(this.picElement);
        let picFrameStyle = window.getComputedStyle(this.picFrameElement);

        let width = this.picElement.naturalWidth;
        let height = this.picElement.naturalHeight;

        let left = parseInt(picStyle.left) + deltaX;
        if (left <= this.#frameLeft && this.#frameRight <= left + width) {
          this.picElement.style.left = `${left}px`;
          this.picFrameElement.style.left = `${
            parseInt(picFrameStyle.left) + deltaX
          }px`;
          this.#movingX = ev.clientX;
        }

        let top = parseInt(picStyle.top) + deltaY;
        if (top <= this.#frameTop && this.#frameBottom <= top + height) {
          this.picElement.style.top = `${top}px`;
          this.picFrameElement.style.top = `${
            parseInt(picFrameStyle.top) + deltaY
          }px`;
          this.#movingY = ev.clientY;
        }
      }
    });

    this.picFrameContainerElement.addEventListener('dragstart', ev => {
      ev.preventDefault();
    });
  }

  show(url) {
    this.picElement.onload = () => {
      const width = this.containerElement.clientWidth;
      const height = this.containerElement.clientHeight;
      const centerX = width / 2;
      const centerY = height / 2;

      this.#frameLeft = Math.max(0, centerX - this.#radius);
      this.#frameRight = Math.min(width, centerX + this.#radius);
      this.#frameTop = Math.max(0, centerY - this.#radius);
      this.#frameBottom = Math.min(height, centerY + this.#radius);

      this.picFrameContainerElement.style.top = `${this.#frameTop}px`;
      this.picFrameContainerElement.style.left = `${this.#frameLeft}px`;

      this.picFrameElement.style.width = `${this.picElement.clientWidth}px`;
      this.picFrameElement.style.height = `${this.picElement.clientHeight}px`;
      this.picFrameElement.style.left = `-${this.#frameLeft}px`;
      this.picFrameElement.style.top = `-${this.#frameTop}px`;
    };

    this.picElement.style.left = '0px';
    this.picElement.style.top = '0px';
    this.picFrameElement.style.left = '0px';
    this.picFrameElement.style.top = '0px';

    this.picElement.src = url;
    this.picFrameElement.src = url;

    super.show();
  }

  get data() {
    const w = this.#radius * 2;
    const h = this.#radius * 2;

    let canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;

    let style = window.getComputedStyle(this.picFrameElement);
    let left = parseInt(style.left);
    let top = parseInt(style.top);

    let ctx = canvas.getContext('2d');
    ctx.drawImage(this.picElement, -left, -top, w, h, 0, 0, w, h);

    return canvas.toDataURL();
  }

  static titleMarkup = `Change profile picture`;
  static bodyMarkup = `
    <div class='pcad-container'>
      <img class='pcad-pic'>
      <div class='pcad-pic-frame-container' draggable>
        <img class='pcad-pic-frame'>
      </div>
    </div>
    `;

  static buttons = {
    accept: {
      label: 'Update',
    },
    cancel: {
      label: 'Cancel',
    },
  };

  static get selectors() {
    const baseSelectors = super.selectors;
    return Object.assign({}, baseSelectors, {
      Container: `${baseSelectors.Body} .pcad-container`,
      Pic: `${baseSelectors.Body} .pcad-pic`,
      PicFrame: `${baseSelectors.Body} .pcad-pic-frame`,
      PicFrameContainer: `${baseSelectors.Body} .pcad-pic-frame-container`,
    });
  }

  static get elements() {
    return {
      containerElement: this.selectors.Container,
      picElement: this.selectors.Pic,
      picFrameElement: this.selectors.PicFrame,
      picFrameContainerElement: this.selectors.PicFrameContainer,
    };
  }

  #frameLeft;
  #frameRight;
  #frameTop;
  #frameBottom;
  #moving;
  #movingX;
  #movingY;
  #radius = 160;
}

window.customElements.define('change-avatar-dialog', ChangeAvatarDialog);

function showChangeAvatarDialog(url) {
  let promise = new AsapPromise();
  let dialog = window.document.querySelector('change-avatar-dialog');
  if (!dialog) {
    dialog = window.document.body.appendChild(
      document.createElement('change-avatar-dialog')
    );
    dialog.onaccept = url => promise.resolve(url);
    dialog.oncancel = () => promise.resolve(null);
  }
  dialog.show(url);
  return promise;
}

export { ChangeAvatarDialog, showChangeAvatarDialog };
